import { Calendar, Clock, Tag, X } from "lucide-react"
import { useEffect, useState } from "react"
import { useAuth } from "../auth/AuthProvider"
import type { Task } from "../types"

type ClientOption = { id: string; name: string }
type ProjectOption = { id: string; name: string }

type AddTaskModalProps = {
  isOpen: boolean
  onClose: () => void
  onSave: (task: Omit<Task, "id" | "createdAt" | "updatedAt">) => void
}

const AddTaskModal = ({ isOpen, onClose, onSave }: AddTaskModalProps) => {
  const { user } = useAuth()
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    status: "todo" as Task["status"],
    priority: "medium" as Task["priority"],
    assigneeId: user?.id || "",
    assignee: user?.name || "",
    projectId: "",
    clientId: "",
    dueDate: "",
    estimatedHours: "",
    tags: [] as string[],
  })
  const [newTag, setNewTag] = useState("")
  const [clients, setClients] = useState<ClientOption[]>([])
  const [projects, setProjects] = useState<ProjectOption[]>([])

  useEffect(() => {
    if (isOpen) {
      fetch('/api/clients').then(r => r.json()).then(d => { if (d.clients) setClients(d.clients) }).catch(() => {})
      fetch('/api/projects').then(r => r.json()).then(d => { if (d.projects) setProjects(d.projects) }).catch(() => {})
    }
  }, [isOpen])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const newTask: Omit<Task, "id" | "createdAt" | "updatedAt"> = {
      title: formData.title,
      description: formData.description || undefined,
      status: formData.status,
      priority: formData.priority,
      assigneeId: formData.assigneeId || undefined,
      assignee: formData.assignee || undefined,
      projectId: formData.projectId || undefined,
      project: formData.projectId
        ? projects.find((p) => p.id === formData.projectId)?.name
        : undefined,
      clientId: formData.clientId || undefined,
      client: formData.clientId
        ? clients.find((c) => c.id === formData.clientId)?.name
        : undefined,
      dueDate: formData.dueDate || undefined,
      estimatedHours: formData.estimatedHours ? parseFloat(formData.estimatedHours) : undefined,
      tags: formData.tags,
    }

    onSave(newTask)
    handleClose()
  }

  const handleClose = () => {
    setFormData({
      title: "",
      description: "",
      status: "todo",
      priority: "medium",
      assigneeId: user?.id || "",
      assignee: user?.name || "",
      projectId: "",
      clientId: "",
      dueDate: "",
      estimatedHours: "",
      tags: [],
    })
    setNewTag("")
    onClose()
  }

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()],
      }))
      setNewTag("")
    }
  }

  const handleRemoveTag = (tag: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((t) => t !== tag),
    }))
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={handleClose}
    >
      <div
        className="relative w-full max-w-2xl rounded-[24px] border border-line bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-line p-6">
          <h2 className="text-xl font-semibold text-slate-900">Create New Task</h2>
          <button
            type="button"
            onClick={handleClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-stone hover:bg-slate-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="max-h-[70vh] overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Title */}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-900">
                Title *
              </label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                placeholder="Task title"
              />
            </div>

            {/* Description */}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-900">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, description: e.target.value }))
                }
                className="w-full resize-none rounded-lg border border-line bg-white p-3 text-sm outline-none focus:border-moss"
                rows={3}
                placeholder="Task description..."
              />
            </div>

            {/* Status and Priority */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-900">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, status: e.target.value as Task["status"] }))
                  }
                  className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                >
                  <option value="todo">To Do</option>
                  <option value="in_progress">In Progress</option>
                  <option value="review">Review</option>
                  <option value="done">Done</option>
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-900">Priority</label>
                <select
                  value={formData.priority}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      priority: e.target.value as Task["priority"],
                    }))
                  }
                  className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
            </div>

            {/* Project and Client */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-900">Project</label>
                <select
                  value={formData.projectId}
                  onChange={(e) => setFormData((prev) => ({ ...prev, projectId: e.target.value }))}
                  className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                >
                  <option value="">None</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-900">Client</label>
                <select
                  value={formData.clientId}
                  onChange={(e) => setFormData((prev) => ({ ...prev, clientId: e.target.value }))}
                  className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                >
                  <option value="">None</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Due Date and Hours */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1.5 flex items-center gap-2 text-sm font-medium text-slate-900">
                  <Calendar className="h-4 w-4" />
                  Due Date
                </label>
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => setFormData((prev) => ({ ...prev, dueDate: e.target.value }))}
                  className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                />
              </div>
              <div>
                <label className="mb-1.5 flex items-center gap-2 text-sm font-medium text-slate-900">
                  <Clock className="h-4 w-4" />
                  Estimated Hours
                </label>
                <input
                  type="number"
                  value={formData.estimatedHours}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, estimatedHours: e.target.value }))
                  }
                  className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                  step="0.25"
                  min="0"
                  placeholder="0"
                />
              </div>
            </div>

            {/* Tags */}
            <div>
              <label className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-900">
                <Tag className="h-4 w-4" />
                Tags
              </label>
              <div className="flex flex-wrap gap-2">
                {formData.tags.map((tag) => (
                  <span
                    key={tag}
                    className="flex items-center gap-1.5 rounded-full bg-moss/10 px-3 py-1 text-xs font-medium text-moss"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="hover:text-moss/70"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="mt-2 flex gap-2">
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault()
                      handleAddTag()
                    }
                  }}
                  placeholder="Add tag (press Enter)"
                  className="flex-1 rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
                />
                <button
                  type="button"
                  onClick={handleAddTag}
                  className="rounded-lg border border-line bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Add
                </button>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-6 flex items-center justify-end gap-3 border-t border-line pt-6">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-lg border border-line bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-lg bg-moss px-4 py-2 text-sm font-medium text-white hover:bg-moss/90"
            >
              Create Task
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AddTaskModal
